import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

declare global {
  interface Window { gtag?: (...args: unknown[]) => void; }
}

function trackForSale(source: string) {
  if (window.gtag) window.gtag('event', 'for_sale_click', { source });
}

const INCLUDED_ITEMS = [
  { icon: 'ri-global-line', title: 'Full Website Ownership', desc: 'Complete transfer of all source code, domain, and assets.' },
  { icon: 'ri-dashboard-line', title: 'Admin Dashboard', desc: 'Manage content, funnels, SEO, and monetization — no coding.' },
  { icon: 'ri-search-eye-line', title: 'SEO Infrastructure', desc: '142 indexed pages, sitemap, schema markup, robots.txt.' },
  { icon: 'ri-article-line', title: 'Content System', desc: '60 articles across 10 cities + 50 city/funding combo pages.' },
  { icon: 'ri-links-line', title: 'Monetization Setup', desc: 'Affiliate links, ad placements, and lead gen funnel ready.' },
  { icon: 'ri-map-pin-line', title: 'City Coverage', desc: '10 major US markets — NY, LA, Miami, Dallas, Chicago + more.' },
];

const WHY_VALUABLE = [
  { stat: '142', label: 'Indexed Pages', icon: 'ri-file-list-3-line' },
  { stat: '10', label: 'US Cities Covered', icon: 'ri-map-pin-line' },
  { stat: '60', label: 'SEO Articles', icon: 'ri-article-line' },
  { stat: '5', label: 'Funding Verticals', icon: 'ri-money-dollar-circle-line' },
];

const MONETIZATION = [
  { icon: 'ri-advertisement-line', title: 'Display Advertising', desc: 'Ready for Google AdSense, Media.net, or any ad network. Header, sidebar, in-content, and footer placements pre-built.' },
  { icon: 'ri-links-line', title: 'Affiliate Income', desc: 'Pre-configured lender affiliate links integrated into the qualification funnel. Swap redirect URLs in seconds via admin panel.' },
  { icon: 'ri-filter-3-line', title: 'Lead Generation', desc: 'Multi-step pre-qualification funnel converts visitors into qualified business loan applicants for lender partners.' },
  { icon: 'ri-store-line', title: 'Expansion Potential', desc: 'Easily expand to more cities, add more lenders, or build out a full marketplace. Architecture supports rapid scaling.' },
];

export default function ForSalePage() {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    trackForSale('contact_form');
    try {
      const body = new URLSearchParams({ name: formData.name, email: formData.email, message: formData.message });
      await fetch('https://readdy.ai/api/form/d74t71ucsbv12mkudal0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0f0f0f]">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/cad1c1875bf289747ac1aea4e8f903d2.jpg"
            alt="Website acquisition opportunity"
            className="w-full h-full object-cover object-top opacity-30"
          />
        </div>
        {/* Red top accent line */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-[#DC2626]"></div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 w-full">
          <div className="max-w-3xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-[#DC2626] text-white text-xs font-bold px-3 py-1.5 rounded-full mb-6 uppercase tracking-widest">
              <div className="w-1.5 h-1.5 bg-white rounded-full animate-ping"></div>
              Available for Acquisition
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
              Turnkey SEO &amp; Lead
              <span className="block text-[#DC2626]">Generation Asset</span>
              <span className="block text-gray-300 text-3xl md:text-4xl font-bold mt-2">Available for Acquisition</span>
            </h1>

            <p className="text-gray-300 text-lg md:text-xl mb-8 leading-relaxed max-w-2xl">
              A fully built, revenue-ready digital platform targeting the business funding niche across 10 major US cities. 142 indexed pages, full admin dashboard, monetization baked in.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#contact"
                onClick={() => trackForSale('hero_cta')}
                className="inline-flex items-center justify-center gap-2 bg-[#DC2626] hover:bg-red-700 text-white text-lg font-bold px-8 py-4 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-send-line"></i>
                Request Details
              </a>
              <a
                href="#whats-included"
                className="inline-flex items-center justify-center gap-2 bg-white/10 border border-white/20 text-white hover:bg-white/20 text-lg font-semibold px-8 py-4 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                What&apos;s Included
                <i className="ri-arrow-down-line"></i>
              </a>
            </div>

            {/* Urgency note */}
            <p className="text-gray-400 text-sm mt-5 flex items-center gap-2">
              <i className="ri-time-line text-[#DC2626]"></i>
              Serious inquiries only. Price available upon request.
            </p>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#DC2626]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {WHY_VALUABLE.map((item) => (
              <div key={item.stat} className="text-center py-2">
                <div className="text-4xl font-black text-white mb-1">{item.stat}</div>
                <div className="flex items-center justify-center gap-1.5 text-red-200 text-sm">
                  <i className={`${item.icon} text-xs`}></i>
                  <span>{item.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT IS THIS WEBSITE */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block text-[#DC2626] font-bold text-sm uppercase tracking-widest mb-3">What You&apos;re Buying</span>
              <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-6 leading-tight">
                A Complete Business<br />Funding Platform
              </h2>
              <p className="text-gray-600 text-lg mb-6 leading-relaxed">
                <strong>BusinessCapitalGuide.net</strong> is a fully operational SEO-driven lead generation website targeting small-to-midsize business owners seeking funding across the United States.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Visitors discover funding options (working capital, term loans, equipment financing, etc.), read educational articles, and go through a multi-step pre-qualification funnel — where qualified leads are referred to a lending partner via affiliate link.
              </p>
              <p className="text-gray-600 leading-relaxed">
                The entire content and monetization system is managed through a built-in admin dashboard — <strong>no coding, no developers required</strong>.
              </p>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-[#DC2626]/10 rounded-3xl"></div>
              <img
                src="https://readdy.ai/api/search-image?query=Laptop%20computer%20screen%20showing%20business%20analytics%20dashboard%20website%20traffic%20SEO%20charts%20graphs%20growing%20data%20professional%20modern%20office%20background%20clean%20minimal&width=800&height=600&seq=forsale002&orientation=landscape"
                alt="Business Capital Guide platform overview"
                className="relative rounded-2xl w-full h-64 md:h-80 object-cover object-top"
              />
              {/* Floating badge */}
              <div className="absolute -bottom-4 -right-4 bg-[#DC2626] text-white px-4 py-3 rounded-xl shadow-lg">
                <div className="text-xs font-bold uppercase tracking-wide">Niche</div>
                <div className="text-lg font-black">Business Funding</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRAFFIC & OPPORTUNITY */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block text-[#DC2626] font-bold text-sm uppercase tracking-widest mb-3">Growth Opportunity</span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">Built to Scale</h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">The SEO architecture is designed to compound. More pages indexed means more organic traffic over time.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: 'ri-search-2-line',
                title: 'Local SEO Dominance',
                desc: 'City-specific pages targeting "business funding [city]" keywords with 10 major markets covered. Easily expand to 50+ cities using the same template structure.',
                highlight: '10 Cities Live',
              },
              {
                icon: 'ri-article-line',
                title: 'Content Authority',
                desc: '60 long-form articles (avg 1,500+ words) covering industry-specific funding topics in each city. Each article feeds organic search traffic into the qualification funnel.',
                highlight: '60 Articles',
              },
              {
                icon: 'ri-links-line',
                title: 'Scalable Architecture',
                desc: 'The programmatic page generation system produces thousands of URL combinations from a single configuration. Adding a city takes minutes, not weeks.',
                highlight: '142 URLs Today',
              },
            ].map((item) => (
              <div key={item.title} className="bg-white rounded-2xl p-6 border border-gray-200">
                <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-xl mb-4">
                  <i className={`${item.icon} text-[#DC2626] text-xl`}></i>
                </div>
                <div className="inline-block bg-[#DC2626] text-white text-xs font-bold px-2.5 py-1 rounded-full mb-3">{item.highlight}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MONETIZATION */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block text-[#DC2626] font-bold text-sm uppercase tracking-widest mb-3">Revenue Streams</span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">Four Ways to Make Money</h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">Multiple monetization layers, all manageable without touching code.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {MONETIZATION.map((item) => (
              <div key={item.title} className="flex gap-4 p-6 bg-gray-50 rounded-2xl border border-gray-200 hover:border-[#DC2626]/30 transition-colors">
                <div className="w-12 h-12 flex items-center justify-center bg-[#DC2626] rounded-xl flex-shrink-0">
                  <i className={`${item.icon} text-white text-xl`}></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section id="whats-included" className="py-20 px-4 bg-[#0f0f0f]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block text-[#DC2626] font-bold text-sm uppercase tracking-widest mb-3">Complete Package</span>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">Everything Transferred on Day One</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">No hidden costs. Full ownership, all assets included.</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {INCLUDED_ITEMS.map((item) => (
              <div key={item.title} className="bg-white/5 border border-white/10 rounded-2xl p-5 hover:border-[#DC2626]/50 transition-colors">
                <div className="w-10 h-10 flex items-center justify-center bg-[#DC2626]/20 rounded-xl mb-4">
                  <i className={`${item.icon} text-[#DC2626] text-lg`}></i>
                </div>
                <h3 className="text-white font-bold text-base mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ADMIN DASHBOARD SECTION */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block text-[#DC2626] font-bold text-sm uppercase tracking-widest mb-3">Zero Tech Skills Needed</span>
              <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-6 leading-tight">
                Full Admin Dashboard<br />
                <span className="text-[#DC2626]">Included &amp; Operational</span>
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                A custom-built admin panel gives you total control over every aspect of the site — without writing a single line of code. Switch lenders, update articles, swap ad scripts, or change SEO settings in minutes.
              </p>
              <ul className="space-y-3">
                {[
                  'Edit all pages, articles, and city content',
                  'Swap affiliate URLs with one click',
                  'Add/remove ad placements by location',
                  'Control SEO titles, meta tags, and sitemap',
                  'Manage the pre-qualification funnel flow',
                  'View activity log of all site changes',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-gray-700 text-sm">
                    <i className="ri-check-line text-[#DC2626] text-lg flex-shrink-0 mt-0.5"></i>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
              <div className="bg-slate-900 rounded-xl p-4 mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-5 h-5 flex items-center justify-center bg-emerald-500 rounded">
                    <i className="ri-briefcase-4-line text-white text-xs"></i>
                  </div>
                  <span className="text-white text-sm font-semibold">BCG Admin</span>
                </div>
                {['Dashboard', 'Cities & Funding', 'Articles', 'Affiliate Links', 'SEO Manager', 'Analytics'].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded text-sm text-slate-400 hover:text-white hover:bg-slate-800 cursor-pointer transition-colors">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-600"></div>
                    {item}
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 text-center">Live admin panel — accessible at /admin</p>
            </div>
          </div>
        </div>
      </section>

      {/* WHY BUY */}
      <section className="py-20 px-4 bg-red-50 border-y-2 border-[#DC2626]/20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block text-[#DC2626] font-bold text-sm uppercase tracking-widest mb-3">Investment Thesis</span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">Why This Asset Is Valuable</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: 'ri-time-line', title: 'Saves Months', desc: 'Months of development, design, content, and SEO work already done. Start monetizing immediately.' },
              { icon: 'ri-funds-line', title: 'High-Value Niche', desc: 'Business funding is one of the highest-paying affiliate niches. CPL rates are consistently strong.' },
              { icon: 'ri-rocket-line', title: 'Scale-Ready', desc: 'The programmatic architecture lets you add cities and funding types in hours, not weeks.' },
              { icon: 'ri-shield-check-line', title: 'No Tech Required', desc: 'The built-in admin dashboard means a non-technical buyer can fully operate this asset from day one.' },
            ].map((item) => (
              <div key={item.title} className="bg-white rounded-2xl p-5 border border-[#DC2626]/20 text-center">
                <div className="w-12 h-12 flex items-center justify-center bg-[#DC2626]/10 rounded-xl mx-auto mb-3">
                  <i className={`${item.icon} text-[#DC2626] text-xl`}></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRANSFER & HOSTING */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block text-[#DC2626] font-bold text-sm uppercase tracking-widest mb-3">Handover Process</span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">Transfer &amp; Hosting Options</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 bg-gray-50 rounded-2xl border border-gray-200">
              <h3 className="font-bold text-gray-900 text-lg mb-3 flex items-center gap-2">
                <i className="ri-file-transfer-line text-[#DC2626]"></i> Full Ownership Transfer
              </h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2"><i className="ri-check-line text-emerald-600 flex-shrink-0 mt-0.5"></i>Complete source code transfer</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-emerald-600 flex-shrink-0 mt-0.5"></i>Domain transfer assistance</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-emerald-600 flex-shrink-0 mt-0.5"></i>Admin credentials handover</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-emerald-600 flex-shrink-0 mt-0.5"></i>30-day onboarding support</li>
              </ul>
            </div>
            <div className="p-6 bg-gray-50 rounded-2xl border border-gray-200">
              <h3 className="font-bold text-gray-900 text-lg mb-3 flex items-center gap-2">
                <i className="ri-server-line text-[#DC2626]"></i> Optional Services
              </h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2"><i className="ri-check-line text-emerald-600 flex-shrink-0 mt-0.5"></i>Managed hosting setup</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-emerald-600 flex-shrink-0 mt-0.5"></i>Ongoing content updates</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-emerald-600 flex-shrink-0 mt-0.5"></i>SEO maintenance package</li>
                <li className="flex items-start gap-2"><i className="ri-check-line text-emerald-600 flex-shrink-0 mt-0.5"></i>Affiliate link management</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT FORM */}
      <section id="contact" className="py-20 px-4 bg-[#0f0f0f]">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-[#DC2626] text-white text-xs font-bold px-3 py-1.5 rounded-full mb-4 uppercase tracking-widest">
              <div className="w-1.5 h-1.5 bg-white rounded-full animate-ping"></div>
              Available Now
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">Request Acquisition Details</h2>
            <p className="text-gray-400 text-lg">Price disclosed to serious buyers only. Fill out the form and we&apos;ll be in touch within 24 hours.</p>
          </div>

          {submitted ? (
            <div className="bg-white/5 border border-[#DC2626]/40 rounded-2xl p-10 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-emerald-500/20 rounded-full mx-auto mb-4">
                <i className="ri-checkbox-circle-line text-emerald-400 text-3xl"></i>
              </div>
              <h3 className="text-white font-bold text-xl mb-2">Inquiry Received!</h3>
              <p className="text-gray-400">We&apos;ll review your inquiry and respond with full details within 24 hours. Thank you for your interest.</p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-white/5 border border-white/10 rounded-2xl p-8 space-y-5"
            >
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">Your Name *</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="John Smith"
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#DC2626] focus:ring-2 focus:ring-[#DC2626]/20"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">Email Address *</label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                  placeholder="john@company.com"
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#DC2626] focus:ring-2 focus:ring-[#DC2626]/20"
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-2">Message / Questions</label>
                <textarea
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={e => setFormData(p => ({ ...p, message: e.target.value }))}
                  placeholder="Tell us about yourself and your interest in this website..."
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-gray-500 text-sm focus:outline-none focus:border-[#DC2626] focus:ring-2 focus:ring-[#DC2626]/20 resize-none"
                  maxLength={500}
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 bg-[#DC2626] hover:bg-red-700 disabled:opacity-60 text-white text-base font-bold py-4 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                {submitting ? (
                  <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                ) : (
                  <><i className="ri-send-plane-fill"></i> Request Details</>
                )}
              </button>

              <p className="text-gray-500 text-xs text-center">
                Your information is kept private and used only to respond to your inquiry.
              </p>
            </form>
          )}

          {/* Back to site */}
          <div className="text-center mt-8">
            <Link to="/" className="text-gray-500 hover:text-gray-300 text-sm transition-colors cursor-pointer">
              &larr; Back to BusinessCapitalGuide.net
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
